import "reflect-metadata"; // must be first
import { CommonModule } from './modules/common.module';
import { type ExtensionConfig, ExtensionContext } from "./common/models/extension-config";
import { type ClassProvider, container, type DependencyContainer, type InjectionToken, Lifecycle } from "tsyringe";
import { Module, ModuleLogger } from './common/module';
import { DashboardTooltipButtonManagerModule } from "./modules/common/dashboard-tooltip-button-manager/dashboard-tooltip-button-manager.module";
import { UrlChangeListenerModule } from "./modules/common/url-change-listener.module";
import { ApiModule } from "./modules/common/api/api.module";
import { RequestInterceptorModule } from "./modules/common/request-interceptor.module";
import { LoginIntegrationModule } from "./modules/login-integration.module";
import { ContactOwnerModule } from "./modules/contact-owner/contact-owner.module";
import { ServerAlertsModule } from "./modules/server-alerts/server-alerts.module";
import { ChangeRoleCommonModule } from "./modules/changerole/changerole-common.module";
import { ChangeRoleToolbarButtonModule } from "./modules/changerole/changerole-toolbarbutton.module";
import { ChangeRoleProfileButtonModule } from "./modules/changerole/changerole-profilebutton.module";
import { RequestAccessModule } from "./modules/request-access.module";
import { GoogleSheetExportModule } from "./modules/google-sheet-export/google-sheet-export.module";
import { VueTestModule } from "./modules/vue-test/vue-test.module";
import { AdvancedExportModule } from "./modules/advanced-export/advanced-export.module";
import { UiModule } from "./modules/common/ui.module";
import { DateReplaceModule } from "./modules/date-replace/date-replace.module";
import { WalkmeModule } from "./modules/netflix/walkme.module";
import { NetflixLoginIntegrationModule } from "./modules/netflix/nx-login-integration.module";

// register the services here
function registerServices() {
    container.register<CommonModule>('module', { useClass: CommonModule }, { lifecycle: Lifecycle.Singleton });
    container.register<ApiModule>('module', { useClass: ApiModule }, { lifecycle: Lifecycle.Singleton });
    container.register<DashboardTooltipButtonManagerModule>('module', { useClass: DashboardTooltipButtonManagerModule }, { lifecycle: Lifecycle.Singleton });
    container.register<UrlChangeListenerModule>('module', { useClass: UrlChangeListenerModule }, { lifecycle: Lifecycle.Singleton });
    container.register<RequestInterceptorModule>('module', { useClass: RequestInterceptorModule }, { lifecycle: Lifecycle.Singleton });
    container.register<LoginIntegrationModule>('module', { useClass: LoginIntegrationModule }, { lifecycle: Lifecycle.Singleton });
    container.register<ContactOwnerModule>('module', { useClass: ContactOwnerModule }, { lifecycle: Lifecycle.Singleton });
    container.register<ServerAlertsModule>('module', { useClass: ServerAlertsModule }, { lifecycle: Lifecycle.Singleton });
    container.register<ChangeRoleCommonModule>('module', { useClass: ChangeRoleCommonModule }, { lifecycle: Lifecycle.Singleton });
    container.register<ChangeRoleToolbarButtonModule>('module', { useClass: ChangeRoleToolbarButtonModule }, { lifecycle: Lifecycle.Singleton });
    container.register<ChangeRoleProfileButtonModule>('module', { useClass: ChangeRoleProfileButtonModule }, { lifecycle: Lifecycle.Singleton });
    container.register<RequestAccessModule>('module', { useClass: RequestAccessModule }, { lifecycle: Lifecycle.Singleton });
    container.register<GoogleSheetExportModule>('module', { useClass: GoogleSheetExportModule }, { lifecycle: Lifecycle.Singleton });
    container.register<AdvancedExportModule>('module', { useClass: AdvancedExportModule }, { lifecycle: Lifecycle.Singleton });
    container.register<UiModule>('module', { useClass: UiModule }, { lifecycle: Lifecycle.Singleton });
    // container.register<DateReplaceModule>('module', { useClass: DateReplaceModule }, { lifecycle: Lifecycle.Singleton });
    container.register<WalkmeModule>('module', { useClass: WalkmeModule }, { lifecycle: Lifecycle.Singleton });
    container.register<NetflixLoginIntegrationModule>('module', { useClass: NetflixLoginIntegrationModule }, { lifecycle: Lifecycle.Singleton });

    // container.register<VueTestModule>('module', { useClass: VueTestModule }, { lifecycle: Lifecycle.Singleton });

}



async function init() {

    // get the config
    // parse URL into config
    let u = document.currentScript!.getAttribute('src')!;
    // if we have a relative url, we fake an absolute one (we only parse query params)
    if (u.startsWith("/")) {
        u = `https://starschema.com${u}`;
    }
    const url = new URL(u);
    window.stm = {
        config: {
            appUrl: parseUrlParam(url, 'appUrl', true)!,
            context: <ExtensionContext>parseUrlParam(url, 'context', true)!,
            isStaticPatch: parseBoolUrlParam(url, 'isStaticPatch', true)!,
            routingPatched: parseBoolUrlParam(url, 'routingPatched', true)!,
            serverId: parseUrlParam(url, 'serverId', true)!,
            serverUrl: parseUrlParam(url, 'serverUrl', true)!,
            serverVersion: parseUrlParam(url, 'serverVersion', true)!,
            licenseFlags: (parseUrlParam(url, 'f', true) || '').split(','),
        },
        modules: {}
    };
    ModuleLogger.pageLoadTime = new Date();
    ModuleLogger.context = window.stm.config.context;

    const log = new ModuleLogger('Core');
    log.i("Tableau Manager integration is active");
    
    // init all modules dynamically
    const modules = container.resolveAll<Module>('module');
    const initedModules: string[] = [];
    for (let m of modules) {
        if ((m.acceptedLicenseFlags.length === 0 || window.stm.config.licenseFlags.some(lf => m.acceptedLicenseFlags.includes(lf))) && m.shouldRun(window.stm.config.context)) {

            await initModule(m, initedModules, log);

        }
    }

}

async function initModule(module: Module, initedModules: string[], log: ModuleLogger): Promise<void> {
    for (let subModule of module.dependantModules ?? []) {
        await initModule(subModule, initedModules, log);
    }

    if (!initedModules.includes(module.name)) {
        log.i('Initializing module: ' + module.name)
        initedModules.push(module.name);
        window.stm.modules[module.name] = module;
        try {
            await module.run();
        } catch (e) {
            log.e('Module threw an error', e);
        }

    }
}

function parseUrlParam(url: URL, name: string, required: boolean): string | undefined {
    const a = url.searchParams.get(name);
    if (!a && required) {
        throw new Error(`Required URL parameter: ${name} not found`);
    }
    if (!a) {
        return undefined;
    }
    // decode again (we encode twice for reasons at Apple AWS LBs)
    return decodeURIComponent(a);;
}
function parseBoolUrlParam(url: URL, name: string, required: boolean): boolean | undefined {
    return parseUrlParam(url, name, required)?.toLowerCase() == 'true';
}


registerServices();
init();
